/**
 * The Raw dock tab (W2-05 easing-templates-raw; DESIGN.md §4.18 "Raw (Full)", U§5 C7): exactly what export writes for
 * the selection, as RPE JSON, editable.
 *
 *  - Shows the first target of the selection (a track's events, notes, a line's own fields, a note control curve);
 *    the "Show" menu offers the others. The text comes from the engine's IR through the exporter's own writer.
 *  - Editing: problems are listed live with their JSON path ("Schema: OK" when there is none); clicking one puts the
 *    caret on the value. Apply (Ctrl+Enter) re-reads the text like an import, refuses anything normalisation would
 *    change (offering the corrected text instead), validates, lifts and applies it as ONE undoable step; Revert
 *    drops the edit. While text is being edited, a changed selection or chart is announced instead of replacing it.
 *  - Events targets also have a read-only events table with "Copy as RPE JSON".
 */

import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import type { PanelProps } from '../../app/registry/types';
import { PROP_SPECS } from '../../core/model/props';
import type { ChartDoc } from '../../core/model/types';
import type { MessageRef } from '../../core/util/types';
import type { DocStore } from '../../state/types';
import type { EncodeStyle, RpeFragmentIr } from '../../formats/rpe/json';
import { Badge, Button, EmptyState, Icon, IconButton, Segmented, Select, Tabs } from '../../ui';
import { useDocVersion, useEditorSlice, useServicesStore, useSettings, useText, type Text } from './hooks';
import { lineColOf } from './jsonPath';
import { m } from './raw.messages';
import s from './Raw.module.css';
import {
  applyRawEdit,
  checkRawText,
  currentFragment,
  encodeFragment,
  rawTargets,
  targetKey,
  targetStillValid,
  type RawCheck,
  type RawProblem,
  type RawTarget,
} from './rawEdit';
import { RawEventsView, type RawEvent } from './RawEventsView';
import { irSourceOf, RAW_COMPACT_KEY, setRawController, styleOf } from './rawSession';

/** Human name of a target ("Position X events · layer 0 · Main"). */
export function targetLabel(target: RawTarget, doc: ChartDoc, text: Pick<Text, 't'>): string {
  const { t } = text;
  const line = doc.lines.get(target.line)?.name ?? String(target.line);
  switch (target.kind) {
    case 'events':
      return PROP_SPECS[target.prop].layered
        ? t(m.targetEvents, { prop: target.prop, layer: target.layer, line })
        : t(m.targetEventsExt, { prop: target.prop, line });
    case 'notes':
      return t(m.targetNotes, { count: target.notes.length, line });
    case 'line':
      return t(m.targetLine, { line });
    case 'controls':
      return t(m.targetControls, { which: target.which, line });
  }
}

interface EditState {
  readonly target: RawTarget;
  /** The exported text the edit started from (in `style`). */
  readonly base: string;
  readonly style: EncodeStyle;
  readonly text: string;
}

const CHECK_DELAY_MS = 200;
const INDENT = '  ';

export function RawPanel(props: PanelProps) {
  const { t } = useText();
  const store = useServicesStore(props.services);
  if (store === null) {
    return (
      <section className={s.panel} data-region="raw" aria-label={t(m.regionLabel)}>
        <EmptyState icon="Braces" title={{ key: m.emptyTitle }} body={{ key: m.noProject }} />
      </section>
    );
  }
  // A new project starts a fresh editor (no edit carried over).
  return <RawEditor key={store.projectId} services={props.services} store={store} />;
}

function RawEditor(props: PanelProps & { readonly store: DocStore }) {
  const { services, store } = props;
  const text = useText();
  const { t, tr } = text;
  useDocVersion(store);
  const selection = useEditorSlice(services, (st) => st.selection);
  const activeLine = useEditorSlice(services, (st) => st.activeLine);
  const focused = useEditorSlice(services, (st) => st.focusedColumn);
  const settings = useSettings(services);
  const style: EncodeStyle = styleOf(settings.ext);
  const [choice, setChoice] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [view, setView] = useState<'json' | 'table'>('json');
  const [checked, setChecked] = useState<{ readonly text: string; readonly result: RawCheck } | null>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const tabLeaves = useRef(false);
  const areaId = useId();

  const src = irSourceOf(services);
  const targets = rawTargets(store.doc, store.idx, selection, activeLine, focused);
  const selected = targets.find((x) => targetKey(x) === choice) ?? targets[0] ?? null;
  const target = edit?.target ?? selected;
  const valid = target !== null && targetStillValid(store.doc, target);
  const current: RpeFragmentIr | null = valid && src !== null ? currentFragment(target, src) : null;
  const exported = current === null ? null : encodeFragment(current, style);
  const shown = edit?.text ?? exported ?? '';
  const dirty = edit !== null && edit.text !== edit.base;
  const selectionMoved = dirty && selected !== null && targetKey(selected) !== targetKey(edit.target);
  const docMoved = dirty && current !== null && encodeFragment(current, edit.style) !== edit.base;
  const label = target !== null ? targetLabel(target, store.doc, text) : '';

  // Live check of the edited text (debounced); the export it is compared with is read when the timer fires.
  const latest = useRef({ current, style });
  useEffect(() => {
    latest.current = { current, style };
  });
  useEffect(() => {
    if (!dirty) return undefined;
    const timer = setTimeout(() => {
      setChecked({
        text: edit.text,
        result: checkRawText(edit.target, edit.text, { doc: store.doc, idx: store.idx, ...latest.current }),
      });
    }, CHECK_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [dirty, edit, store]);
  const check = dirty && checked?.text === edit.text ? checked.result : null;

  const toast = (msg: MessageRef, kind: 'info' | 'success' | 'warning' | 'error' = 'info'): void => {
    services.ui.toast(msg, { kind });
  };

  const revert = (): void => {
    setEdit(null);
  };

  const apply = (): void => {
    if (edit === null) return;
    if (!dirty) {
      toast({ key: m.appliedNothing });
      return;
    }
    const fresh = src === null ? null : currentFragment(edit.target, src);
    const result = checkRawText(edit.target, edit.text, { doc: store.doc, idx: store.idx, current: fresh, style });
    setChecked({ text: edit.text, result });
    const frag = result.frag;
    if (frag === null) {
      const firstError = result.problems.find((p) => p.level === 'error');
      if (firstError !== undefined) goTo(firstError);
      return;
    }
    const out = store.transact({ label: { key: m.txApply, params: { target: label } }, command: 'raw.apply' }, (tx) =>
      applyRawEdit(tx, { target: edit.target, next: frag, current: fresh }),
    );
    if (!out.ok) {
      toast(out.reason, 'error');
      return;
    }
    for (const note of out.notes) toast(note, 'info');
    services.ui.toast(
      { key: m.applied, params: { count: out.value.changed } },
      { kind: 'success', undo: out.value.changed > 0 },
    );
    setEdit(null);
  };

  /** Copies RPE JSON: the editor's text (header button) or the exported events the read-only table lists. */
  const copyText = (value: string | null): void => {
    if (value === null || value === '') return;
    void navigator.clipboard.writeText(value).then(
      () => {
        toast({ key: m.copied, params: { target: label } }, 'success');
      },
      (err: unknown) => {
        toast({ key: m.copyFailed, params: { reason: err instanceof Error ? err.message : String(err) } }, 'error');
      },
    );
  };

  const goTo = (p: RawProblem): void => {
    const el = areaRef.current;
    if (el === null || p.offset === null) return;
    el.focus();
    el.setSelectionRange(p.offset, p.offset);
  };

  // Commands (raw.apply / raw.revert) act on the mounted panel.
  useEffect(() => {
    setRawController({ dirty, target, apply, revert });
  });
  useEffect(
    () => () => {
      setRawController(null);
    },
    [],
  );

  const change = (next: string): void => {
    if (target === null || exported === null) return;
    setEdit((cur) => (cur === null ? { target, base: exported, style, text: next } : { ...cur, text: next }));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      apply();
      return;
    }
    if (e.key === 'Escape') {
      tabLeaves.current = true;
      return;
    }
    if (e.key === 'Tab' && !tabLeaves.current && !e.ctrlKey && !e.altKey && !e.metaKey) {
      const el = e.currentTarget;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const value = el.value;
      e.preventDefault();
      if (e.shiftKey) {
        const lineStart = value.lastIndexOf('\n', start - 1) + 1;
        if (value.startsWith(INDENT, lineStart)) {
          change(value.slice(0, lineStart) + value.slice(lineStart + INDENT.length));
          requestAnimationFrame(() => {
            el.setSelectionRange(Math.max(lineStart, start - INDENT.length), Math.max(lineStart, end - INDENT.length));
          });
        }
        return;
      }
      change(value.slice(0, start) + INDENT + value.slice(end));
      requestAnimationFrame(() => {
        el.setSelectionRange(start + INDENT.length, start + INDENT.length);
      });
      return;
    }
    tabLeaves.current = false;
  };

  const problems = check?.problems ?? [];
  const errors = problems.filter((p) => p.level === 'error').length;
  const warnings = problems.length - errors;
  const events: readonly RawEvent[] = current?.kind === 'events' ? current.ir : [];
  const baked = events.some((e) => e.origin === 'bakePiece');
  const absentControl = target?.kind === 'controls' && current?.kind === 'controls' && current.ir.length === 0;
  const schema =
    errors > 0
      ? { kind: 'error' as const, text: t(m.schemaProblems, { count: errors }) }
      : warnings > 0
        ? { kind: 'warning' as const, text: t(m.schemaWarnings, { count: warnings }) }
        : { kind: 'ok' as const, text: t(m.schemaOk) };
  const applyReason: MessageRef | null = !dirty ? { key: m.unchanged } : null;

  return (
    <section className={s.panel} data-region="raw" aria-label={t(m.regionLabel)}>
      <header className={s.header}>
        <div className={s.headerTitle}>
          <strong>{t(m.panelTitle)}</strong>
          <Badge kind="full" />
          <span className={s.muted}>{t(m.subtitle)}</span>
        </div>
        <div className={s.headerActions}>
          <span className={s.schema} data-state={schema.kind} role="status">
            <Icon
              name={schema.kind === 'ok' ? 'CircleCheck' : schema.kind === 'warning' ? 'TriangleAlert' : 'CircleAlert'}
              size={14}
            />
            {schema.text}
          </span>
          <span className={s.muted}>{dirty ? t(m.edited) : t(m.unchanged)}</span>
          <Button variant="primary" size="sm" icon="Check" disabledReason={applyReason} onClick={apply}>
            {t(m.applyHint)}
          </Button>
          <Button size="sm" icon="Undo2" disabledReason={dirty ? null : { key: m.unchanged }} onClick={revert}>
            {t(m.revert)}
          </Button>
          <IconButton
            icon="Copy"
            size="sm"
            label={{ key: m.copy }}
            onClick={() => {
              copyText(shown);
            }}
          />
        </div>
      </header>
      {targets.length === 0 && edit === null ? (
        <EmptyState icon="Braces" title={{ key: m.emptyTitle }} body={{ key: m.emptyBody }} />
      ) : (
        <>
          <div className={s.toolbar}>
            <Select
              label={t(m.showLabel)}
              value={target === null ? 'mixed' : targetKey(target)}
              disabled={dirty}
              options={targets.map((x) => ({ value: targetKey(x), label: targetLabel(x, store.doc, text) }))}
              onChange={(v) => {
                setChoice(v);
                setEdit(null);
              }}
            />
            <Segmented
              label={t(m.formatLabel)}
              value={style.pretty ? 'pretty' : 'compact'}
              options={[
                { value: 'pretty', label: t(m.pretty) },
                { value: 'compact', label: t(m.compact) },
              ]}
              onChange={(v) => {
                services.settings.setExt(RAW_COMPACT_KEY, v === 'compact');
              }}
            />
            {target?.kind === 'events' && (
              <Tabs
                variant="pill"
                label={t(m.viewLabel)}
                value={view}
                onChange={(v) => {
                  setView(v === 'table' ? 'table' : 'json');
                }}
                tabs={[
                  { value: 'json', label: t(m.viewJson), icon: 'Braces' },
                  { value: 'table', label: t(m.viewTable), icon: 'Table' },
                ]}
              />
            )}
          </div>
          {selectionMoved && (
            <div className={s.banner} role="status">
              <span>{t(m.selectionChanged)}</span>
              <Button size="sm" onClick={revert}>
                {t(m.showSelection)}
              </Button>
            </div>
          )}
          {docMoved && (
            <div className={s.banner} role="status">
              <span>{t(m.docChanged)}</span>
              <Button size="sm" onClick={revert}>
                {t(m.reload)}
              </Button>
            </div>
          )}
          {baked && <p className={s.note}>{t(m.bakedNote)}</p>}
          {absentControl && <p className={s.note}>{t(m.controlAbsent)}</p>}
          {current === null && <p className={s.note}>{t(m.emptyAbsent)}</p>}
          {view === 'table' && target?.kind === 'events' ? (
            <RawEventsView
              events={events}
              label={label}
              onCopy={() => {
                copyText(exported);
              }}
              onReveal={(ev) => {
                const key = ev.srcKey;
                const k = key === null ? undefined : store.doc.keys.get(key);
                if (k === undefined) return;
                void services.registry.run('nav.reveal', {
                  beat: { w: k.t.w, n: k.t.n, d: k.t.d },
                  line: target.line,
                  keys: [k.id],
                });
              }}
            />
          ) : (
            <div className={s.editor}>
              <label htmlFor={areaId} className={s.editorLabel}>
                {t(m.editorLabel)} · {label}
              </label>
              <textarea
                id={areaId}
                ref={areaRef}
                className={s.area}
                value={shown}
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                data-pending-edit={dirty || undefined}
                aria-invalid={errors > 0 || undefined}
                onChange={(e) => {
                  change(e.target.value);
                }}
                onKeyDown={onKeyDown}
              />
              {problems.length > 0 && (
                <ul className={s.problems} aria-label={t(m.problemsLabel)}>
                  {problems.map((p, i) => {
                    const where = p.offset === null ? null : lineColOf(shown, p.offset);
                    const msg = tr(p.message);
                    return (
                      <li key={`${p.path}-${String(i)}`} data-level={p.level}>
                        <Icon name={p.level === 'error' ? 'CircleAlert' : 'TriangleAlert'} size={14} />
                        <button
                          type="button"
                          className={s.problemButton}
                          disabled={p.offset === null}
                          title={p.path === '' ? undefined : t(m.goToProblem, { path: p.path })}
                          onClick={() => {
                            goTo(p);
                          }}
                        >
                          {p.path === ''
                            ? t(m.problemWhole, { message: msg })
                            : t(m.problemAt, { path: p.path, message: msg })}
                          {where !== null && (
                            <span className={s.muted}>
                              {' '}
                              ({where.line}:{where.col})
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {check?.corrected !== null && check?.corrected !== undefined && (
                <div className={s.banner} role="status">
                  <span>{t(m.correctedHint)}</span>
                  <Button
                    size="sm"
                    onClick={() => {
                      const corrected = check.corrected;
                      if (corrected !== null) change(corrected);
                    }}
                  >
                    {t(m.useCorrected)}
                  </Button>
                </div>
              )}
              <p className={s.note}>{t(m.footnote)}</p>
            </div>
          )}
        </>
      )}
    </section>
  );
}
